import React, { createContext, useContext, useReducer, useEffect, useCallback, useState } from 'react';
import axios from 'axios';

const CartContext = createContext();

// Set base URL for API calls
const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
axios.defaults.baseURL = API_BASE_URL;

const initialState = {
  items: [],
  total: 0,
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item._id === action.payload._id);
      if (existingItem) {
        return state; // Item already in cart
      }
      return {
        ...state,
        items: [...state.items, action.payload],
        total: state.total + action.payload.price,
      };
    case 'REMOVE_ITEM':
      const itemToRemove = state.items.find(item => item._id === action.payload);
      return {
        ...state,
        items: state.items.filter(item => item._id !== action.payload),
        total: state.total - (itemToRemove ? itemToRemove.price : 0),
      };
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        total: 0,
      };
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload.items || [],
        total: action.payload.total || 0,
      };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isValidating, setIsValidating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Validate cart items and remove invalid ones
  const validateCartItems = useCallback(async () => {
    if (state.items.length === 0 || isValidating) return;

    setIsValidating(true);
    try {
      const validItems = [];
      let newTotal = 0;

      for (const item of state.items) {
        try {
          // Check if course still exists by making a request
          const response = await axios.get(`/api/courses/${item._id}`);
          if (response.status === 200) {
            validItems.push(item);
            newTotal += item.price || 0;
          }
        } catch (error) {
          console.warn(`Course ${item._id} no longer exists, removing from cart`);
        }
      }

      if (validItems.length !== state.items.length) {
        dispatch({ type: 'LOAD_CART', payload: { items: validItems, total: newTotal } });
        console.log(`Removed ${state.items.length - validItems.length} invalid items from cart`);
      }
    } catch (error) {
      console.error('Error validating cart items:', error);
    } finally {
      setIsValidating(false);
    }
  }, [dispatch, state.items, isValidating]);

  // Load cart from localStorage on mount (only once)
  useEffect(() => {
    if (isInitialized) return; // Prevent reloading if already initialized
    
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        // Validate cart data structure
        if (cartData && Array.isArray(cartData.items)) {
          dispatch({ type: 'LOAD_CART', payload: cartData });
          console.log('Cart loaded from localStorage:', cartData.items.length, 'items');
          
          // Validate cart items after loading (run once)
          // Temporarily disabled for debugging
          // setTimeout(() => {
          //   validateCartItems();
          // }, 1000); // Small delay to ensure other components are loaded
        } else {
          console.warn('Invalid cart data in localStorage, starting with empty cart');
          localStorage.removeItem('cart'); // Clean up invalid data
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem('cart'); // Clean up corrupted data
      }
    }
    setIsInitialized(true);
  }, [isInitialized]);

  // Listen for logout events and clear cart
  useEffect(() => {
    const handleLogout = () => {
      console.log('User logged out, clearing cart');
      dispatch({ type: 'CLEAR_CART' });
    };

    // Listen for custom logout event
    window.addEventListener('user-logout', handleLogout);
    
    return () => {
      window.removeEventListener('user-logout', handleLogout);
    };
  }, [dispatch]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    console.log('Cart state changed, should save to localStorage:', state);
    if (state.items.length > 0 || state.total > 0) {
      localStorage.setItem('cart', JSON.stringify(state));
      console.log('Cart saved to localStorage:', state.items.length, 'items');
    } else {
      // Remove cart from localStorage if empty
      localStorage.removeItem('cart');
      console.log('Empty cart, removed from localStorage');
    }
  }, [state]);

  const addToCart = (course) => {
    // Check if course is already in cart
    const existingItem = state.items.find(item => item._id === course._id);
    if (existingItem) {
      return { success: false, message: '이미 장바구니에 있는 강의입니다.' };
    }
    
    dispatch({ type: 'ADD_ITEM', payload: course });
    return { success: true, message: '장바구니에 추가되었습니다.' };
  };

  const removeFromCart = (courseId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: courseId });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartItemCount = () => {
    return state.items.length;
  };

  const isInCart = (courseId) => {
    return state.items.some(item => item._id === courseId);
  };

  const isEnrolled = (courseId) => {
    // This would need to be implemented with user context
    // For now, we'll rely on the course detail page to check enrollment
    return false;
  };

  // Debug function to check cart state
  const debugCart = () => {
    console.log('Current cart state:', {
      items: state.items,
      total: state.total,
      itemCount: state.items.length
    });
    console.log('localStorage cart:', localStorage.getItem('cart'));
  };

  const value = {
    items: state.items,
    total: state.total,
    addToCart,
    removeFromCart,
    clearCart,
    getCartItemCount,
    isInCart,
    isEnrolled,
    debugCart,
    validateCartItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 